import { Component, OnDestroy, OnInit, computed, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  PROFILE_TAB_NOTIFICATIONS,
  PROFILE_TAB_QUERY_PARAM
} from '../../../core/components/user-profile/profile-tabs';
import { NotificationSegment } from '../../shared/services/notification/notification-list.store';
import { NotificationService } from '../../shared/services/notification/notification.service';
import { NOTIFICATIONS_TAB_QUERY_PARAM, notificationSegmentFor } from './notifications-tabs';

/** Die Seite laedt 20 auf einmal — sie hat den Platz dafuer, das Popover nicht (#736 §11). */
const PAGE_SIZE = 20;

/**
 * Der volle Bestand des In-App-Streams (#736 §11).
 *
 * Existiert, weil das Popover keine Hoehenbegrenzung hat und mit jedem Nachladen aus dem Viewport
 * lief (Befund C). Es zeigt seitdem nur noch einen Auszug und verlinkt hierher.
 *
 * Der Listenzustand liegt in einer EIGENEN Store-Instanz, nicht in der des Popovers
 * (Entscheidung 12) — sonst wuerde ein Nachladen hier den Stand der Glocke ueberschreiben und
 * umgekehrt.
 */
@Component({
  selector: 'app-notifications-page',
  templateUrl: './notifications-page.html',
  styleUrls: ['./notifications-page.scss']
})
export class NotificationsPageComponent implements OnInit, OnDestroy {

  // inject() statt Konstruktor-Parametern, damit die Feld-Initialisierer unten garantiert in
  // Deklarationsreihenfolge nach den Abhaengigkeiten laufen — dasselbe Muster wie im
  // NotificationService.
  private readonly notificationService = inject(NotificationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly list = this.notificationService.createListStore(PAGE_SIZE);

  /** Ungedeckelt — anders als der Badge an der Glocke, der bei "9+" endet (§3, Entscheidung 13). */
  readonly unreadCount = this.notificationService.unreadCount;

  readonly emptyStateKey = computed(() =>
    this.list.segment() === 'all'
      ? 'notificationCenterComponent.emptyStateFirstTime'
      : 'notificationCenterComponent.emptyState'
  );

  /** Panel-Reihenfolge in notifications-page.html: 0 = Ungelesen, 1 = Alle. */
  readonly activeTabIndex = computed(() => (this.list.segment() === 'all' ? 1 : 0));

  private routeSubscription: Subscription;

  ngOnInit(): void {
    // Ueber queryParamMap statt snapshot, damit eine Navigation auf denselben Screen mit anderem
    // Parameter greift — Angular verwendet die Komponenteninstanz dabei wieder.
    // setSegment() laedt die erste Seite gleich mit.
    this.routeSubscription = this.route.queryParamMap.subscribe((params) => {
      this.list.setSegment(notificationSegmentFor(params.get(NOTIFICATIONS_TAB_QUERY_PARAM)));
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
  }

  setSegment(segment: NotificationSegment): void {
    this.list.setSegment(segment);
  }

  /**
   * p-tabView meldet `activeIndexChange` auch dann, wenn der Index programmatisch gesetzt wurde.
   * Ohne diese Wache liefe der Wechsel doppelt: setSegment laedt die erste Seite, das Signal
   * aendert activeTabIndex, der TabView meldet erneut.
   */
  onTabChange(index: number): void {
    const segment: NotificationSegment = index === 1 ? 'all' : 'unread';
    if (segment === this.list.segment()) {
      return;
    }
    this.setSegment(segment);
  }

  markAllAsRead(): void {
    this.list.markAllRead();
  }

  /** Das Zahnrad fuehrt auf die Benachrichtigungs-Einstellungen (#735). */
  goToSettings(): void {
    this.router
      .navigate(['/profile'], {
        queryParams: { [PROFILE_TAB_QUERY_PARAM]: PROFILE_TAB_NOTIFICATIONS }
      })
      .catch((error) => console.warn('notifications-page: Navigation abgewiesen', error));
  }

}
